import { LazyQueryExecFunction } from '@apollo/client';
import { GetHierarchyQuery, GetHierarchyQueryVariables } from '@modules/search';
import {
    DynamicRegex,
    FieldSetting,
    MandatoryForSaveCondition,
    NameObject,
    NodeContent,
    NodeContentO,
    NodeContentValue,
    SearchFilterOperator,
} from '@src/__generated__/types';
import { DynamicRegexComparatorOptions } from '@src/_new/modules/product/components/ProductInfoViewer/FormComponent/components/ContentTypeForm/DynamicRegexField/DynamicRegexField';
import { MandatoryForSaveComparatorOptions } from '@src/_new/modules/product/components/ProductInfoViewer/FormComponent/components/ContentTypeForm/MandatoryForSaveElement/MandatoryForSaveElement';
import {
    CheckGlItemsQuery,
    CheckGlItemsQueryVariables,
    CheckInvalidGlsQuery,
    CheckInvalidGlsQueryVariables,
} from '@src/utils/cgt/cgtQuery.generated';

import { isDefined } from '../helpers/isDefined';
import { GetNodeCodeQuery, GetNodeCodeQueryVariables } from '../Hierarchy.generated';

export type MappedValues = {
    contentType: string;
    contentTypeGroup: string;
    contentTypeGroupIndex: number;
    value: NodeContentValue[];
    mandatoryForSave?: MandatoryForSaveCondition;
    regex?: string;
    dynamicRegex?: DynamicRegex[];
    multiLanguage?: boolean;
    multiValue?: boolean;
    type: string;
    name?: NameObject[];
};

export const checkDependantGls = async ({
    nodeContents,
    dirtyFields,
    hierarchyId,
    cgtModelHierarchyId,
    glHierarchyId,
    priceListHierarchyId,
    getCodes,
    company,
}: {
    hierarchyId: string;
    cgtModelHierarchyId?: string;
    glHierarchyId?: string;
    priceListHierarchyId?: string;
    nodeContents: NodeContentO[];
    dirtyFields: NodeContent[];
    getCodes: LazyQueryExecFunction<GetNodeCodeQuery, GetNodeCodeQueryVariables>;
    company?: string;
}) => {
    if (hierarchyId === cgtModelHierarchyId && company === 'cgt') {
        const savedSecondaryPriceLists =
            nodeContents.find(c => c.contentType === 'secondary_price_list')?.value?.[0]?.value ??
            [];

        const updatedSecondaryPriceLists = dirtyFields.find(
            f => f.contentTypeGroup === 'base_info' && f.contentType === 'secondary_price_list',
        )?.value?.[0]?.value;

        const [primaryPriceList] =
            dirtyFields.find(
                f => f.contentTypeGroup === 'base_info' && f.contentType === 'primary_price_list',
            )?.value?.[0]?.value ??
            nodeContents.find(c => c.contentType === 'primary_price_list')?.value?.[0]?.value ??
            [];

        // removed secondary price lists
        const priceListsDiff: string[] = updatedSecondaryPriceLists
            ? savedSecondaryPriceLists.filter(p => !updatedSecondaryPriceLists.includes(p))
            : [];

        const baseMachines: {
            id: string;
            priceListToUse: { priceList: string; priceListZMAB?: string };
        }[] =
            dirtyFields.find(
                f => f.contentTypeGroup === 'logistic_groups' && f.contentType === 'base_machine',
            )?.value?.[0]?.value ??
            nodeContents.find(c => c.contentType === 'base_machine')?.value?.[0]?.value ??
            [];

        const accessories: {
            id: string;
            priceListToUse: { priceList: string; priceListZMAB?: string };
        }[] =
            dirtyFields.find(
                f => f.contentTypeGroup === 'logistic_groups' && f.contentType === 'accessories',
            )?.value?.[0]?.value ??
            nodeContents.find(c => c.contentType === 'accessories')?.value?.[0]?.value ??
            [];

        const glPriceLists = new Map<string, string[]>();

        const dependantGls = baseMachines.concat(accessories).filter(gl => {
            if (gl && typeof gl === 'object' && 'priceListToUse' in gl) {
                const foundPriceLists = Object.values(gl.priceListToUse)
                    .flatMap(p => p?.split('$$'))
                    .filter(p => priceListsDiff.includes(p));
                if (
                    foundPriceLists.length &&
                    primaryPriceList &&
                    !foundPriceLists.includes(primaryPriceList)
                ) {
                    glPriceLists.set(gl.id, foundPriceLists);
                    return true;
                }
            }
            return false;
        });

        if (dependantGls.length) {
            const glNodes =
                (
                    await getCodes({
                        variables: {
                            hierarchiesId: glHierarchyId,
                            nodeFilter: { ids: dependantGls.map(gl => gl.id) },
                        },
                    })
                ).data?.hierarchies?.[0].nodes ?? [];

            const priceListIds = new Set(...glPriceLists.values());

            const priceListNodes =
                (
                    await getCodes({
                        variables: {
                            hierarchiesId: priceListHierarchyId,
                            nodeFilter: { ids: Array.from(priceListIds) },
                        },
                    })
                ).data?.hierarchies?.[0].nodes ?? [];

            const priceListNodesMap = new Map(priceListNodes.map(p => [p.id, p.code]));

            const gls = glNodes
                .map(gl => {
                    const glPriceListIds = glPriceLists.get(gl.id);
                    if (!glPriceListIds) return undefined;
                    const priceListCodes: string[] = [];

                    glPriceListIds.forEach(priceListId => {
                        const priceListCode = priceListNodesMap.get(priceListId);
                        if (priceListCode) {
                            priceListCodes.push(priceListCode);
                        }
                    });
                    if (!priceListCodes.length) return undefined;
                    return {
                        id: gl.id,
                        glCode: gl.code,
                        priceListCodes,
                    };
                })
                .filter(isDefined);

            return {
                gls,
                priceLists: Array.from(priceListNodesMap.values()),
            };
        }
    }
    return null;
};

export const checkInvalidGls = async ({
    hierarchyId,
    updatedFields,
    cgtModelHierarchyId,
    modelId,
    fetchInvalidGls,
    nodeContents,
    company,
}: {
    hierarchyId: string;
    updatedFields: MappedValues[];
    cgtModelHierarchyId?: string;
    modelId?: string;
    fetchInvalidGls?: LazyQueryExecFunction<CheckInvalidGlsQuery, CheckInvalidGlsQueryVariables>;
    nodeContents: NodeContentO[];
    company?: string;
}) => {
    const formPriceListId = updatedFields.find(f => f.contentType === 'primary_price_list')
        ?.value?.[0].value?.[0];

    const savedPriceListId = nodeContents.find(f => f.contentType === 'primary_price_list')
        ?.value?.[0].value?.[0];

    if (
        company === 'cgt' &&
        hierarchyId === cgtModelHierarchyId &&
        formPriceListId &&
        savedPriceListId !== formPriceListId &&
        modelId &&
        fetchInvalidGls
    ) {
        const res = await fetchInvalidGls({
            variables: {
                modelId,
                priceListId: formPriceListId,
            },
        });

        return res.data?.invalidGls;
    }

    return [];
};

export const checkDuplicateGl = async ({
    hierarchyId,
    modelId,
    updatedFields,
    dirtyFields,
    checkGLItems,
    glHierarchyId,
    glItemsField,
    company,
    nodeId,
}: {
    hierarchyId: string;
    modelId?: string;
    updatedFields: MappedValues[];
    dirtyFields: MappedValues[];
    checkGLItems: LazyQueryExecFunction<CheckGlItemsQuery, CheckGlItemsQueryVariables>;
    glHierarchyId?: string;
    glItemsField?: FieldSetting;
    company?: string;
    nodeId?: string;
}) => {
    if (hierarchyId === glHierarchyId && modelId && company === 'cgt') {
        const currentItems = dirtyFields.find(f => f.contentType === glItemsField?.contentTypeCode);

        const glType = updatedFields.find(
            f => f.contentTypeGroup === 'base_info' && f.contentType === 'type',
        )?.value?.[0].value?.[0];
        const itemValues = currentItems?.value?.[0]?.value;

        const res = await checkGLItems({
            variables: {
                modelId,
                itemValues,
                glType,
                glNodeId: nodeId,
            },
        });

        if (res.error) {
            throw new Error(res.error.message);
        }

        return res.data?.glItems;
    }
    return null;
};

export type RemovedBaseMachineItem = {
    id: string;
    code: string;
    itemCode?: string;
};

// link values are either the plain node id or an object carrying it alongside quantity/position
const getLinkValueId = (value: unknown): string | null => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'id' in value) {
        const { id } = value as { id?: unknown };
        return typeof id === 'string' ? id : null;
    }
    return null;
};

/**
 * A ZMMB gl ("Macchina base") derives its description/denomination and its SAP MM_MBASE class
 * from the linked item whose `item_info-system_type` is '2'. Removing that item is allowed but
 * needs an explicit confirmation, so collect the base machine items the save would unlink.
 */
export const checkRemovedBaseMachineItems = async ({
    hierarchyId,
    glHierarchyId,
    priceListHierarchyId,
    glItemsField,
    updatedFields,
    dirtyFields,
    nodeContents,
    fetchNodesWithContents,
}: {
    hierarchyId: string;
    glHierarchyId?: string;
    priceListHierarchyId?: string;
    glItemsField?: FieldSetting;
    updatedFields: MappedValues[];
    dirtyFields: NodeContent[];
    nodeContents: NodeContentO[];
    fetchNodesWithContents: LazyQueryExecFunction<GetHierarchyQuery, GetHierarchyQueryVariables>;
}): Promise<RemovedBaseMachineItem[]> => {
    if (hierarchyId !== glHierarchyId || !priceListHierarchyId) return [];

    const glType = updatedFields.find(
        f => f.contentTypeGroup === 'base_info' && f.contentType === 'type',
    )?.value?.[0]?.value?.[0];

    if (glType !== 'ZMMB') return [];

    const itemsContentType = glItemsField?.contentTypeCode;
    const dirtyItems = dirtyFields.find(f => f.contentType === itemsContentType);

    // nothing was changed on the items field, so nothing was removed
    if (!itemsContentType || !dirtyItems) return [];

    const savedIds = (
        nodeContents.find(c => c.contentType === itemsContentType)?.value?.[0]?.value ?? []
    )
        .map(getLinkValueId)
        .filter(isDefined);

    const updatedIds = new Set(
        (dirtyItems.value?.[0]?.value ?? []).map(getLinkValueId).filter(isDefined),
    );

    const removedIds = savedIds.filter(id => !updatedIds.has(id));

    if (!removedIds.length) return [];

    const res = await fetchNodesWithContents({
        variables: {
            ids: [priceListHierarchyId],
            pagination: { first: removedIds.length },
            sort: [],
            filter: [
                {
                    field: 'id',
                    operator: SearchFilterOperator.IsAnyOf,
                    value: removedIds,
                },
            ],
        },
        fetchPolicy: 'no-cache',
    });

    return (res.data?.searchHierarchies?.nodes?.edges ?? [])
        .map(edge => edge.node)
        .filter(node => {
            // decided client side: a `item_info-system_type` content filter would match
            // `item_info-system_type_description` too (content filters match by prefix)
            const systemType = node.contents?.find(
                c => c.contentTypeGroup === 'item_info' && c.contentType === 'system_type',
            )?.value?.[0]?.value?.[0];
            return systemType === '2';
        })
        .map(node => ({
            id: node.id,
            code: node.code,
            itemCode: node.contents?.find(
                c => c.contentTypeGroup === 'item_info' && c.contentType === 'item_code',
            )?.value?.[0]?.value?.[0],
        }));
};

export const checkIfMandatoryFieldIsSet = (field: MappedValues, initialValues: MappedValues[]) => {
    if (field.mandatoryForSave) {
        const { comparator, value, isMandatory } = field.mandatoryForSave;
        if (!isMandatory) return false;
        const [ctg, ct] = field.mandatoryForSave?.option?.split('-') ?? ['', ''];
        // checks if mandatoryForSave is defined upon condition
        // if not, simply checks if value is defined
        if (isMandatory && (!field.mandatoryForSave?.option || !comparator || !value)) {
            return field.multiLanguage
                ? !field.value.some(val => val?.value?.[0])
                : !field?.value?.length ||
                      !field.value?.[0] ||
                      !field.value?.[0]?.value ||
                      !field.value?.[0]?.value?.[0];
        }
        const foundValue = initialValues.find(
            v => v.contentTypeGroup === ctg && v.contentType === ct,
        );
        // if condition is met, check if value is defined
        if (foundValue && comparator?.length && value?.length) {
            if (
                (comparator === MandatoryForSaveComparatorOptions.Equals &&
                    value === foundValue?.value?.[0].value?.[0]) ||
                (comparator === MandatoryForSaveComparatorOptions.NotEquals &&
                    value !== foundValue?.value?.[0].value?.[0])
            ) {
                if (field.multiLanguage) {
                    return !field.value.some(val => val?.value?.[0]);
                }
                return (
                    !field?.value?.length ||
                    !field.value?.[0] ||
                    !field.value?.[0]?.value ||
                    !field.value?.[0]?.value?.[0]
                );
            }
        }
        return false;
    }
    return false;
};

export const checkIfRegexIsMatched = (field: MappedValues, initialValues: MappedValues[]) => {
    let notMatched = false;
    // check if generic regex is defined
    // if so, test value against regex
    if (field.regex) {
        const re = new RegExp(field.regex);
        notMatched = field.multiLanguage
            ? !field.value.every(val => re.test(val?.value?.[0]))
            : !re.test(field.value?.[0]?.value?.[0]);
    }
    // if the previous regex was successful, check for dynamiRegex
    if (!notMatched && field.dynamicRegex?.length) {
        // test every regex, matching the condition defined
        // at the first failing regex, exit
        field.dynamicRegex.every(regex => {
            const [ctg, ct] = regex.option.split('-') ?? ['', ''];
            const { regex: re, comparator, value } = regex;
            const foundValue = initialValues.find(
                v => v.contentTypeGroup === ctg && v.contentType === ct,
            );
            if (foundValue && re?.length && comparator?.length && value?.length) {
                if (
                    (comparator === DynamicRegexComparatorOptions.Equals &&
                        value === foundValue?.value?.[0].value?.[0]) ||
                    (comparator === DynamicRegexComparatorOptions.NotEquals &&
                        value !== foundValue?.value?.[0].value?.[0])
                ) {
                    const rex = new RegExp(re);
                    const result = field.multiLanguage
                        ? !field.value.every(val => rex.test(val?.value?.[0]))
                        : !rex.test(field.value?.[0]?.value?.[0]);
                    notMatched = result;
                    return result;
                }
            }
            return true;
        });
    }
    return notMatched;
};
